import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { Edit2, Check, X } from 'react-feather';
import { PiGenderFemale, PiGenderMale } from 'react-icons/pi';

import {
    ValidationForm,
    Grid,
    Cell,
    Box,
    Container,
    PanelSteps,
    FieldText,
    FieldSelect,
    FieldHeight,
    FieldPhone,
    FieldDate,
    FieldRadio,
    H2,
    P,
    Type,
    Btn,
    FieldCheckbox,
    BtnDot,
} from '@saladbob/sassafras'



import { fetchUser } from '../../hooks/useSwr';
import { options as tshirtOptions } from '../../data/tshirtOptions';
import xYearsFromNow from '../../utils/xYearsFromNow';
import { useTeam, useTeamDispatch } from '../providers/TeamProvider';
import { modal } from '../../components/providers/ModalProvider';
import { options as experienceOptions, keys as experienceKeys } from '../../data/experienceOptions';
import BackBtn from '../buttons/BackBtn';

import { User } from '../../types';
import provinces from '../../data/provinces';

type StatLineProps = {
    stat: string;
    value: string | React.ReactNode;
    canEdit?: boolean;
    field?: React.ReactNode;
    onSubmit?: (form: any, data: any) => void;
};

type PlayerCardProps = {
    color: string;
    color2: string;
    player: User;
    step: number;
    setStep: (step: number) => void;
    onVerify: (player: User) => void;
    onExit?: () => void;
};

type Props = {
    onSubmit: (players: User[]) => void;
    onExit: () => void;
};

// function to calculate height from inches
const calculateHeight = (inches: number) => {
    const feet = Math.floor(inches / 12);
    const inchesRemainder = inches % 12;
    return `${feet}'${inchesRemainder}"`;
};

// function to format phone number
const formatPhone = (phone: number | string) => {
    if (!phone) return '';
    const phoneString = phone.toString();
    const areaCode = phoneString.slice(0, 3);
    const prefix = phoneString.slice(3, 6);
    const lineNumber = phoneString.slice(6, 10);
    return `(${areaCode}) ${prefix}-${lineNumber}`;
};

const formatAddress = (player: User) => {
    const street = [player.address1, player.address2].filter(Boolean).join(' ');
    const cityState = [player.city, player.state].filter(Boolean).join(', ');
    const region = [cityState, player.postcode].filter(Boolean).join(' ');
    const country = player.country === 'US'
        ? 'United States'
        : player.country === 'CA'
            ? 'Canada'
            : player.country;

    return [street, region, country].filter(Boolean).join(', ') || 'Add address';
};

const removeLeadingUnderscore = (str) => {
    if (typeof str !== 'string') return str;
    return str.startsWith('_') ? str.slice(1) : str;
}

const genderIcons = {
    m: (<PiGenderMale size={40} />),
    f: (<PiGenderFemale size={40} />),
}

const StatLine: React.FC<StatLineProps> = ({stat, value, canEdit, field, onSubmit}) => {
    const saveData = (form, data) => {
        onSubmit(form, data);
        modal.close();
    };

    const editField = () => {
        modal.open({
            title: `EDIT ${stat}`,
            content: (
                <ValidationForm onSubmit={saveData}>
                    {field}
                    <Grid mt="md" columns="1fr 1fr" width="300px" gridGap="lg" margin="auto">
                        <Btn
                            type="button"
                            size="sm"
                            outline
                            color="neutral"
                            iconLeft={(<X size="16" />)}
                            onClick={modal.close}
                            style={{ maxHeight: '2.5rem' }}
                        >
                            CANCEL
                        </Btn>
                        <Btn
                            type="submit"
                            size="sm"
                            color="secondary"
                            iconLeft={(<Check size="16" />)}
                            style={{ maxHeight: '2.5rem' }}
                        >
                            SAVE
                        </Btn>
                    </Grid>
                </ValidationForm>
            ),
        })
    };

    return (
        <Grid bdWidth={[0, 0, '1px', 0]} bdColor="neutral200" padding={['sm', 0]} align="center" columns="30% 1fr min-content">
            <Cell column={{ phone: 'span 3', desktop: 'span 1' }} minHeight="32px" align="center">
                <P weight="bold" mb="0">
                    {stat}:
                </P>
            </Cell>
            <Cell column={{ phone: 'span 2', desktop: 'span 1' }} minHeight="32px" align="center">
                <P mb="0">
                    {value}
                </P>
            </Cell>
        
            <Cell align="flex-start">
                {canEdit && (
                    <BtnDot
                        size="md"
                        outline
                        color="secondary"
                        ms="sm"
                        onClick={editField}
                    >
                        <Edit2 size="16" />
                    </BtnDot>
                )}
            </Cell>
        </Grid>
    );
}

const PlayerCard: React.FC<PlayerCardProps> = ({ color, step, setStep, player, onExit, onVerify }) => {
    const { user } = fetchUser();
    const [newPlayer, setNewPlayer] = useState(player);
    const addToTeam = useMemo(() => {
        if (newPlayer?.addToTeam) {
            
            const att = typeof newPlayer.addToTeam[0] === 'string' ? newPlayer.addToTeam : newPlayer.addToTeam.map(u => u.id);

            if (att.includes(user.id)) {
                return true;
            }
        }
        return false;
    }, [player?.addToTeam]);

    const canEdit = player.canEdit || !player.id;
    const hasAddress = Boolean(
        newPlayer.address1
        || newPlayer.address2
        || newPlayer.city
        || newPlayer.state
        || newPlayer.postcode
        || newPlayer.country
    );

    // Save data from form
    const saveData= (form, data) => {
        setNewPlayer({
            ...newPlayer,
            ...data,
        });
    };

    // Verify player
    const verify = () => {
        onVerify(newPlayer);
    };

    return (
      <>
        <Box
          depth="xs"
          padding="md"
          bgColor="neutral50"
          mb="xl"
          bdColor={color}
          bdWidth={[0, 0, 0, "xl"]}
        >
          <Grid columns={{ phone: "1fr", laptop: "1fr 1fr" }} gridGap="md">
            <Cell column={{ phone: null, laptop: "span 2" }}>
              <Box justify="space-between">
                <P font="h2" weight="bold" mb="0">
                  {newPlayer.fname} {newPlayer.lname}
                </P>
                <Type color="neutral">{genderIcons[newPlayer.gender]}</Type>
              </Box>
            </Cell>
            <Cell>
              <StatLine
                stat="HEIGHT"
                value={calculateHeight(newPlayer.height)}
                onSubmit={saveData}
                canEdit={canEdit}
                field={
                  <FieldHeight
                    size="sm"
                    value={newPlayer.height}
                    name="height"
                    minimal
                  />
                }
              />
              <StatLine
                stat="EXP"
                value={
                  experienceKeys[removeLeadingUnderscore(newPlayer.experience)]
                }
                onSubmit={saveData}
                canEdit={canEdit}
                field={
                  <FieldSelect
                    size="sm"
                    value={newPlayer.experience}
                    placeholder="Select"
                    options={experienceOptions}
                    name="experience"
                    rules={{ required: true }}
                  />
                }
              />
              <StatLine
                stat="DOB"
                value={dayjs(newPlayer.dob).format("MM/DD/YYYY")}
                canEdit={canEdit}
                onSubmit={saveData}
                field={
                  <FieldDate
                    name="dob"
                    size="sm"
                    value={dayjs(newPlayer.dob).format("MM/DD/YYYY")}
                    rule={{
                      required: true,
                      date: {
                        min: new Date(1900, 1, 1),
                        max: xYearsFromNow(5),
                      },
                    }}
                  />
                }
              />
            </Cell>
            <Cell>
              <StatLine
                stat="EMAIL"
                value={newPlayer.email}
                canEdit={canEdit}
                onSubmit={saveData}
                field={
                  <FieldText
                    name="email"
                    size="sm"
                    type="email"
                    value={newPlayer.email}
                    placeholder="Email"
                    rules={{
                      email: true,
                      checkEmail: async (value) => {
                        const res = await fetch(
                          `${process.env.NEXT_PUBLIC_API_URL}/user/account-exists/${value}`,
                        ).then((r) => r.json());
                        return {
                          valid: res.valid,
                          message:
                            "An account with this email already exists, leave blank or use a unique email.",
                        };
                      },
                    }}
                  />
                }
              />
              <StatLine
                stat="PHONE"
                value={formatPhone(newPlayer.phone)}
                onSubmit={saveData}
                canEdit={canEdit}
                field={
                  <FieldPhone
                    name="phone"
                    size="sm"
                    value={newPlayer.phone}
                    rules={{
                      required: true,
                    }}
                  />
                }
              />
              <StatLine
                stat="TSHIRT SIZE"
                value={newPlayer.tshirt}
                onSubmit={saveData}
                canEdit={canEdit}
                field={
                  <FieldRadio
                    font="p"
                    value={newPlayer.tshirt}
                    options={tshirtOptions}
                    label="T-Shirt Size"
                    name="tshirt"
                    columns="repeat(auto-fit, minmax(140px, 1fr))"
                    rules={{ required: true }}
                  />
                }
              />
            </Cell>
            {(canEdit || hasAddress) && (
              <Cell column={{ phone: null, laptop: "span 2" }}>
                <StatLine
                  stat="ADDRESS"
                  value={formatAddress(newPlayer)}
                  onSubmit={saveData}
                  canEdit={canEdit}
                  field={
                    <Grid columns="1fr 1fr 1fr 1fr" gridGap="sm">
                      <Cell column="span 2">
                        <FieldText
                          size="sm"
                          value={newPlayer.address1}
                          placeholder="Address"
                          name="address1"
                          rules={{
                            minlength: 3,
                            required: true,
                          }}
                        />
                      </Cell>
                      <Cell column="span 2">
                        <FieldText
                          size="sm"
                          value={newPlayer.address2}
                          placeholder="Address 2"
                          name="address2"
                          rules={{
                            minlength: 3,
                          }}
                        />
                      </Cell>
                      <Cell column="span 2">
                        <FieldText
                          size="sm"
                          value={newPlayer.city}
                          placeholder="City"
                          name="city"
                          rules={{
                            minlength: 3,
                            required: true,
                          }}
                        />
                      </Cell>
                      <Cell>
                        <FieldSelect
                          placeholder="State"
                          value={newPlayer.state}
                          font="control"
                          size="sm"
                          options={provinces}
                          name="state"
                          rules={{
                            minlength: 2,
                            required: true,
                          }}
                        />
                      </Cell>
                      <Cell>
                        <FieldText
                          size="sm"
                          value={newPlayer.postcode}
                          type="tel"
                          placeholder="ZIP"
                          name="postcode"
                          minlength={5}
                          maxlength={5}
                          rules={{
                            minlength: 5,
                            maxlength: 5,
                          }}
                        />
                      </Cell>
                      <Cell column="span 4">
                        <FieldSelect
                          placeholder="Select"
                          value={newPlayer.country}
                          font="p"
                          options={[
                            {
                              label: "United States",
                              value: "US",
                            },
                            {
                              label: "Canada",
                              value: "CA",
                            },
                          ]}
                          label="Country"
                          name="country"
                          rules={{ required: true, minlength: 2 }}
                        />
                      </Cell>
                    </Grid>
                  }
                />
              </Cell>
            )}
          </Grid>
        </Box>
        <Box align="center" mb="lg">
          <ValidationForm onSubmit={verify}>
            {(player.canEdit || !player.id) && (
              <>
                <FieldCheckbox
                  name="verify"
                  value="1"
                  size="xs"
                  label={
                    <P>
                      I verify that the above information is accurate and
                      submitted with the player’s consent. I acknowledge that if
                      any of the information is found to be false, both the
                      player and I may be disqualified from the tournament
                      without refund and may be blacklisted from future events.
                    </P>
                  }
                  rules={{ required: true }}
                />
                <Btn>Verify</Btn>
              </>
            )}
            {player.id && !player.canEdit && addToTeam && (
              <>
                <P weight="bold">
                  You don’t have access to make changes to this player’s
                  account. We’ve reached out to them directly to confirm their
                  information. If something looks wrong, it’s best to contact
                  the player to resolve it.
                </P>
                <FieldCheckbox
                  name="verify"
                  value="1"
                  size="xs"
                  label={
                    <P>
                      I acknowledge that I am not authorized to make changes to
                      this player’s account. If the player fails to verify their
                      information by the start of the tournament, and it is
                      found to be inaccurate, both the player and team may be
                      disqualified from the tournament without refund.
                    </P>
                  }
                  rules={{ required: true }}
                />
                <Btn>Continue</Btn>
              </>
            )}
            {player.id && !player.canEdit && !addToTeam && (
              <>
                <P>
                  <i>
                    Some of the player's information is locked because they are
                    currently registered to a tournament that is past deadline.
                  </i>
                </P>
                <FieldCheckbox
                  name="verify"
                  value="1"
                  size="xs"
                  label={
                    <P>
                      I verify that the above information is accurate and
                      submitted with the player’s consent.
                      <br />I acknowledge that if any of the information is
                      found to be false, both the player and I may be
                      disqualified from the tournament without refund and may be
                      blacklisted from future events.
                    </P>
                  }
                  rules={{ required: true }}
                />
                <Btn>Verify</Btn>
              </>
            )}
          </ValidationForm>
        </Box>
        {step > 0 && (
          <BackBtn
            onClick={() => {
              setStep(step - 1);
            }}
          />
        )}
        {step === 0 && <BackBtn onClick={onExit} />}
      </>
    );
};

const VerifyPlayers: React.FC<Props> = ({ onSubmit, onExit }) => {
    const team = useTeam();
    const teamDispatch = useTeamDispatch();
    const [step, setStep] = useState(0);
    // get players that are not verified
    const players = useMemo(() => {
        return team.players.reduce((reducer,player, index) => {
            if (!player.verified) {
                reducer.push({ ...player, position: index });
            }
            return reducer;
        }, []);
    }, []);

    const verifyPlayer = (data) => {
        const currentPlayer = players[step];
        const newPlayers = [...team.players];
        newPlayers[currentPlayer.position] = {...data, verified: true, verifiedAt: new Date()};

        teamDispatch({
            type: 'replace-players',
            players: newPlayers,
        });

        if (step < players.length - 1) {
            setStep(step + 1);
        } else {
            onSubmit(newPlayers);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    return (
        <Container>
            {team && (<H2 font="jumbo" mb="lg">{team.name}</H2>)}
            <PanelSteps step={ step } travel={100}>
                {players.map((player) => (
                    <PlayerCard
                        color={team.color}
                        color2={team.color2}
                        player={player}
                        onVerify={verifyPlayer}
                        onExit={onExit}
                        step={step}
                        setStep={setStep}
                    />
                ))}
            </PanelSteps>
        </Container>
    );
}

export default VerifyPlayers;
